/**
 * Session Timeout Component
 * 
 * Automatically logs out users after a period of inactivity.
 * Shows a warning dialog before logging out.
 * 
 * Uses localStorage to persist activity timestamps across browser throttling.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isDesktopRuntime, navigateOrRedirect } from '../../utils/desktopNavigation';

// Configuration
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
const WARNING_BEFORE_LOGOUT_MS = 5 * 60 * 1000; // 5 minutes warning
const CHECK_INTERVAL_MS = 10 * 1000; // Check every 10 seconds
const STORAGE_KEY = 'xpherium_last_activity';

// Activity events to track
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'touchstart',
  'click',
  'wheel'
];
// Note: keep this list user-intent focused (no generic scroll), otherwise
// automated UI scrolling can unintentionally prevent inactivity logout.

const SessionTimeout = ({ children }) => {
  const { t } = useTranslation('auth');
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_BEFORE_LOGOUT_MS);
  const countdownIntervalRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  // Get last activity from localStorage
  const getLastActivity = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return parseInt(stored, 10);
    }
    return Date.now();
  }, []);

  // Update last activity timestamp in localStorage
  const updateActivity = useCallback(() => {
    if (isLoggingOutRef.current) return;
    
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, now.toString());
    
    // If warning is NOT showing, just update. 
    // If warning IS showing, user must click the button to extend (not just any activity)
  }, []);

  // Extend session (user clicked "Stay signed in")
  const extendSession = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, now.toString());
    setShowWarning(false);
    setTimeRemaining(WARNING_BEFORE_LOGOUT_MS);
    
    // Clear countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    console.log('[Session] Extended session via button click');
  }, []);

  // Handle logout
  const handleLogout = useCallback((isManual = false) => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    
    console.log('[Session] Logging out', isManual ? 'manually' : 'due to inactivity');
    setShowWarning(false);
    
    // Clear countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    // Clear the activity timestamp
    localStorage.removeItem(STORAGE_KEY);
    
    // Determine redirect based on current product
    const currentProduct = localStorage.getItem('currentProduct') || user?.signupProduct || 'intelligence-navigator';
    const landingPages = {
      'intelligence-navigator': 'https://www.xpherium.com/intelligence-navigator',
      'autopilot': 'https://www.xpherium.com/autopilot',
      'data-iq': 'https://www.xpherium.com/data-iq',
      'knowledge-iq': 'https://www.xpherium.com/knowledge-iq'
    };
    const redirectUrl = landingPages[currentProduct] || landingPages['intelligence-navigator'];
    
    // Perform logout (with fallback cleanup in case provider call throws)
    try {
      logout();
    } catch (error) {
      console.error('[Session] Logout failed, applying fallback cleanup:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
    
    // Redirect to appropriate landing page
    if (isDesktopRuntime) {
      navigateOrRedirect('/');
    } else {
      window.location.href = redirectUrl;
    }
  }, [logout, user]);

  // Show warning and start countdown
  const showLogoutWarning = useCallback(() => {
    if (showWarning || isLoggingOutRef.current) return;
    
    console.log('[Session] Showing warning, logout in 5 minutes unless user extends');
    setShowWarning(true);
    setTimeRemaining(WARNING_BEFORE_LOGOUT_MS);
    
    // Start countdown
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          handleLogout();
          return 0;
        }
        return newTime;
      });
    }, 1000);
  }, [handleLogout, showWarning]);

  // Main inactivity check - runs on interval AND on visibility change
  const checkInactivity = useCallback(() => {
    if (!user || isLoggingOutRef.current) return;
    
    const lastActivity = getLastActivity();
    const now = Date.now();
    const timeSinceActivity = now - lastActivity;
    
    console.log('[Session] Check: inactive for', Math.round(timeSinceActivity / 60000), 'minutes');
    
    // If past the full timeout, logout immediately
    if (timeSinceActivity >= INACTIVITY_TIMEOUT_MS) {
      console.log('[Session] Past full timeout, logging out immediately');
      handleLogout();
      return;
    }
    
    // If past warning threshold, show warning
    const warningThreshold = INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS;
    if (timeSinceActivity >= warningThreshold && !showWarning) {
      showLogoutWarning();
    }
  }, [user, getLastActivity, handleLogout, showWarning, showLogoutWarning]);

  // Tell the server this account is still in the app, so the owner view
  // can measure visit length. Customers never see that view.
  useEffect(() => {
    if (!user?.id || isLoggingOutRef.current) return undefined;
    const api = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';
    const send = () => {
      if (isLoggingOutRef.current) return;
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) return;
      fetch(`${api}/auth/presence`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ path: window.location.pathname }),
      }).catch(() => {});
    };
    send();
    const intervalId = setInterval(send, 2 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [user?.id]);

  // Initialize last activity on mount
  useEffect(() => {
    if (!user) return;
    
    // Always reset on fresh authenticated session so stale timestamps from a
    // previous session don't trigger immediate post-login logout.
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    
    // Reset logging out flag
    isLoggingOutRef.current = false;
  }, [user]);

  // Check for inactivity periodically
  useEffect(() => {
    if (!user) return;
    
    // Check immediately on mount
    checkInactivity();
    
    // Check periodically
    const intervalId = setInterval(checkInactivity, CHECK_INTERVAL_MS);
    
    return () => clearInterval(intervalId);
  }, [user, checkInactivity]);

  // Check on tab visibility change (handles browser throttling)
  useEffect(() => {
    if (!user) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Session] Tab became visible, checking inactivity...');
        checkInactivity();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, checkInactivity]);

  // Check on window focus (backup for visibility change)
  useEffect(() => {
    if (!user) return;
    
    const handleFocus = () => {
      console.log('[Session] Window focused, checking inactivity...');
      checkInactivity();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, checkInactivity]);

  // Track user activity
  useEffect(() => {
    if (!user) return;
    
    // Add event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      
      // Clear countdown on unmount
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [user, updateActivity]);

  // Format time remaining
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      
      {/* Warning Modal */}
      <AnimatePresence>
        {showWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md mx-4 shadow-2xl"
            >
              <div className="text-center">
                {/* Icon */}
                <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
                
                {/* Title */}
                <h2 className="text-2xl font-bold text-white mb-2">
                  {t('sessionTimeoutTitle')}
                </h2>
                
                {/* Message */}
                <p className="text-slate-400 mb-4">
                  {t('sessionTimeoutWarning')}
                </p>
                
                {/* Countdown */}
                <div className="text-4xl font-mono font-bold text-amber-400 mb-6">
                  {formatTime(timeRemaining)}
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleLogout(true)}
                    className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('signOutNow')}
                  </button>
                  <button
                    onClick={extendSession}
                    className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t('staySignedIn')}
                  </button>
                </div>
                
                {/* Note */}
                <p className="text-xs text-slate-500 mt-4">
                  {t('staySignedInNote')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SessionTimeout;
